import { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';
import { ContactType } from './typings';

const USERNAME_REGEX = /^[A-Za-z0-9\-._@+]+$/;
const SAFE_TEXT_REGEX = /^[\p{L} .'-]*$/u;
const SAFE_STREET_REGEX = /^[\p{L}\p{N} .,'#-]*$/u;
const SAFE_POSTAL_CODE_REGEX = /^[\p{L}0-9 -]*$/u;

// Shared field validators
const userNameField = Yup.string().trim()
    .required("User Name is required")
    .max(128, "User Name max length is 128 characters")
    .matches(USERNAME_REGEX, "User login name can only contain letters, digits, or -._@+");

const emailField = Yup.string().trim()
    .required("Email is required")
    .max(256, "Email max length is 256 characters")
    .email("Email must be valid");

const firstNameField = Yup.string().trim()
    .required("First Name is required")
    .max(64, "First Name max length is 64 characters")
    .matches(SAFE_TEXT_REGEX, "First Name contains invalid characters");

const lastNameField = Yup.string().trim()
    .required("Last Name is required")
    .max(64, "Last Name max length is 64 characters")
    .matches(SAFE_TEXT_REGEX, "Last Name contains invalid characters");

const phoneNumberField = Yup.string().trim()
    .test('phone-number', 'Phone number is invalid', (value) => !value ? true : isValidPhoneNumber(value));

export const UserPersonalDetailsSchema = Yup.object().shape({
    firstName: firstNameField,
    lastName: lastNameField,
    phoneNumber: phoneNumberField,
});

export const RoleSchema = Yup.object().shape({
    name: Yup.string().trim()
        .required("Role name is required")
        .max(256, "Role name max length is 256 characters"),
    description: Yup.string().trim()
        .max(255, "Description max length is 255 characters"),
});

// Auth0 organization name: lowercase letters/digits/hyphen/underscore, starting with a letter or digit.
export const OrganizationSchema = Yup.object().shape({
    name: Yup.string().trim()
        .required("Organization name is required")
        .max(50, "Organization name max length is 50 characters")
        .matches(/^[a-z0-9][a-z0-9_-]*$/, "Only lowercase letters, digits, hyphens and underscores are allowed, starting with a letter or digit"),
    displayName: Yup.string().trim()
        .required("Display Name is required")
        .max(255, "Display Name max length is 255 characters"),
});

export const UserProfileDataSchema = Yup.object().shape({
    userName: userNameField,
    email: emailField,
    firstName: firstNameField,
    lastName: lastNameField,
    phoneNumber: phoneNumberField,
});

export const BulkUserRowSchema = Yup.object().shape({
    userName: userNameField,
    email: emailField,
    firstName: firstNameField,
    lastName: lastNameField,
    phoneNumber: phoneNumberField,
});

export const InviteRowSchema = Yup.object().shape({
    email: emailField,
});

export const UserNameSchema = Yup.object().shape({
    userName: userNameField,
});

export const EmailSchema = Yup.object().shape({
    email: emailField,
});

export const UserContactSchema = Yup.object().shape({
    contactType: Yup.number()
        .min(1, "Contact Type is required"),
    contact: Yup.string().trim()
        .required("Contact is required")
        .max(128, "Contact max length is 128 characters")
        .when('contactType', {
            is: ContactType.Email,
            then: (schema) => schema.email("Email address is invalid"),
            otherwise: (schema) => schema.test('phone-number', 'Phone number is invalid', (value) => !value ? true : isValidPhoneNumber(value)),
        }),
    phoneExtension: Yup.string().trim()
        .when('contactType', {
            is: ContactType.Phone,
            then: (schema) => schema
                .max(3, "Phone extension max length is 3 digits")
                .matches(/^\d*$/, "Digits only"),
            otherwise: (schema) => schema,
        })
});

export const AddressSchema = Yup.object().shape({
    street1: Yup.string().trim()
        .required("Street 1 is required")
        .max(128, "Street 1 max length is 128 characters")
        .matches(SAFE_STREET_REGEX, "Street 1 contains invalid characters"),
    street2: Yup.string().trim()
        .max(64, "Street 2 max length is 64 characters")
        .matches(SAFE_STREET_REGEX, { message: "Street 2 contains invalid characters", excludeEmptyString: true }),
    city: Yup.string().trim()
        .required("City is required")
        .max(128, "City max length is 128 characters")
        .matches(SAFE_TEXT_REGEX, "City contains invalid characters"),
    state: Yup.string().trim()
        .required("State is required")
        .max(128, "State max length is 128 characters")
        .matches(SAFE_TEXT_REGEX, "State contains invalid characters"),
    postalCode: Yup.string().trim()
        .required("Postal Code is required")
        .max(16, "Code max length is 16 characters")
        .matches(SAFE_POSTAL_CODE_REGEX, "Postal Code contains invalid characters"),
    countryAlpha3Code: Yup.string().trim()
        .required("Country is required")
        .length(3, "Country code must be exactly 3 characters")
});

export const OrganizationMemberSchema = Yup.object().shape({
    organizationId: Yup.number()
        .min(1, "Organization is required")
});
